import { motion } from "framer-motion";
import { Clapperboard, Tv } from "lucide-react";
import { PHOTOS } from "@/data/content";

const PROJECTS = [
  {
    icon: Clapperboard,
    badge: "In Production — Late 2026",
    title: "A View From The Top",
    note: "Working title",
    desc: "Susan's own syndicated television talk show, currently in production with Spark TV / Howie Mandel's Studios in Burbank, California — conversations on healing, resilience, and transformation coming to screens everywhere.",
    testid: "tv-card-view-from-top",
    image: PHOTOS.nycMemorial,
    imageAlt: "Susan Grau in New York City",
    imagePos: "50% 25%",
  },
  {
    icon: Tv,
    badge: "Featured 2026",
    title: "The Drew Barrymore Show",
    note: "On the Drew set",
    desc: "Susan joined Valerie Bertinelli on The Drew Barrymore Show for a celebrated March 2026 conversation on grief, resilience, and hope — with more television ahead.",
    testid: "tv-card-drew-barrymore",
    image: PHOTOS.drewNyc,
    imageAlt: "Susan Grau outside The Drew Barrymore Show studios in New York",
    imagePos: "50% 45%",
  },
];

export default function Television() {
  return (
    <section id="television" data-testid="television-section" className="relative overflow-hidden bg-[#F7F1E6] py-28 text-[#6B5D4F] lg:py-40">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{ backgroundImage: `url(${PHOTOS.orbs})`, backgroundSize: "cover", backgroundPosition: "center" }}
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute inset-0 bg-[#F7F1E6]/60" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
        >
          Chapter 05 — On Television
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
          className="mt-6 max-w-3xl font-display text-4xl font-light leading-tight tracking-tighter sm:text-5xl lg:text-6xl"
        >
          The Camera Loves <span className="italic text-[#C5A059]">Her Message</span>
        </motion.h2>

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {PROJECTS.map((p, i) => (
            <motion.div
              key={p.title}
              data-testid={p.testid}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.76, 0, 0.24, 1] }}
              whileHover={{ y: -6 }}
              className="group relative flex min-h-[24rem] flex-col justify-between overflow-hidden border border-[#6B5D4F]/15 bg-[#F7F1E6]/70 p-10 backdrop-blur-md transition-shadow duration-500 hover:shadow-[0_30px_60px_-20px_rgba(197,160,89,0.25)]"
            >
              <div className="flex items-start justify-between">
                <p.icon size={30} strokeWidth={1.2} className="text-[#C5A059]" />
                <span className="border border-[#C5A059]/50 px-4 py-1.5 font-body text-[0.55rem] font-medium uppercase tracking-[0.35em] text-[#C5A059]">
                  {p.badge}
                </span>
              </div>
              {p.image && (
                <div className="my-8 overflow-hidden border border-[#6B5D4F]/10">
                  <img
                    src={p.image}
                    alt={p.imageAlt}
                    data-testid="tv-drew-photo"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ objectPosition: p.imagePos || "50% 45%" }}
                  />
                </div>
              )}
              <div>
                <p className="font-script text-2xl text-[#C5A059]/80">{p.note}</p>
                <h3 className="mt-2 font-display text-4xl font-light tracking-tight md:text-5xl">{p.title}</h3>
                <p className="mt-5 max-w-md font-body text-sm font-light leading-relaxed text-[#6B5D4F]/65 md:text-base">
                  {p.desc}
                </p>
              </div>
              <div
                className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full opacity-30 transition-transform duration-700 group-hover:scale-125"
                style={{ background: "radial-gradient(circle, rgba(197,160,89,0.5), transparent 70%)" }}
                aria-hidden="true"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
